import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { useApp } from '../context/AppContext';
import { Prospects, Guests as GuestsApi } from '../lib/api';
import { SOURCES, PROSPECT_LABELS } from '../lib/prospectConfig';
import { toast } from '../hooks/use-toast';
import { UserPlus, Sparkles } from 'lucide-react';

export default function NewProspectDialog({ open, onOpenChange, onCreated }) {
  const { activeShow, activeShowId, filteredGuests, refreshGuests } = useApp();
  const [form, setForm] = useState({
    name: '', email: '', prEmail: '', phone: '', company: '', role: '', location: '',
    source: 'linkedin', sourceDetail: '', notes: '', labels: ['podcast_guest']
  });
  const [saving, setSaving] = useState(false);
  const [promotingId, setPromotingId] = useState(null);

  const potentialGuests = (filteredGuests || []).filter(g => g.status === 'potential');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const reset = () => setForm({ name: '', email: '', prEmail: '', phone: '', company: '', role: '', location: '', source: 'linkedin', sourceDetail: '', notes: '', labels: ['podcast_guest'] });

  const toggleLabel = (v) => setForm(f => {
    const has = f.labels.includes(v);
    const next = has ? f.labels.filter(l => l !== v) : [...f.labels, v];
    return { ...f, labels: next.length ? next : f.labels };
  });

  const promoteOne = async (g) => {
    setPromotingId(g.id);
    try {
      const res = await GuestsApi.promoteToProspect(g.id, { source: 'other', sourceDetail: 'Promoted from Potential Guest' });
      toast({ title: 'Promoted to Prospect', description: `${g.name} is now in your Prospects pipeline.` });
      await refreshGuests();
      onCreated?.(res.prospect);
    } catch (e) {
      toast({ title: 'Promotion failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setPromotingId(null);
    }
  };

  const submit = async () => {
    if (!form.name || !form.email) { toast({ title: 'Name and email required' }); return; }
    setSaving(true);
    try {
      const created = await Prospects.create({ ...form, showId: activeShowId });
      toast({ title: 'Prospect added' });
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Failed to add' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add prospect to {activeShow.name}</DialogTitle>
          <DialogDescription className="sr-only">Add a new prospect or promote an existing potential guest into the prospects pipeline.</DialogDescription>
        </DialogHeader>

        {potentialGuests.length > 0 && (
          <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 mb-2">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-violet-600" />
              <div className="text-[12.5px] font-medium text-violet-900">Promote a Potential Guest</div>
              <div className="text-[11px] text-violet-700 ml-auto">{potentialGuests.length} ready</div>
            </div>
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
              {potentialGuests.map(g => (
                <div key={g.id} className="flex items-center gap-2 bg-white border border-violet-100 rounded px-2.5 py-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-stone-900 truncate">{g.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">
                      {g.email || '—'}{g.company ? ` · ${g.company}` : ''}{g.location ? ` · ${g.location}` : ''}
                    </div>
                  </div>
                  <Button
                    data-testid={`promote-potential-${g.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => promoteOne(g)}
                    disabled={promotingId === g.id}
                    className="h-7 border-violet-300 text-violet-700 hover:bg-violet-100 text-[11.5px]"
                  >
                    <UserPlus className="w-3 h-3 mr-1" /> {promotingId === g.id ? '…' : 'Promote'}
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-stone-500 mt-2">Promoted prospects start with source <b>Other</b> — edit it from the prospect detail.</div>
            <div className="relative flex items-center gap-2 mt-3 -mb-1">
              <div className="flex-1 h-px bg-stone-200" />
              <div className="text-[10.5px] uppercase text-stone-400 tracking-wider">or add new</div>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Prospect type</Label>
            <div className="flex gap-2 mt-1">
              {PROSPECT_LABELS.map(l => {
                const active = form.labels.includes(l.value);
                return (
                  <button key={l.value} type="button" data-testid={`new-prospect-label-${l.value}`} onClick={() => toggleLabel(l.value)}
                    className="px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors"
                    style={active ? { background: `${l.color}22`, color: l.color, borderColor: `${l.color}66` } : { borderColor: '#e7e5e4', color: '#78716c' }}>
                    {active ? '✓ ' : ''}{l.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[10.5px] text-stone-400 mt-1">A prospect can carry both labels — the type decides which reach-out letter group applies.</div>
          </div>
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
          <div><Label>PR / Agent email</Label><Input type="email" value={form.prEmail} onChange={(e) => set('prEmail', e.target.value)} placeholder="optional" /></div>
          <div className="col-span-2"><Label>Cell phone</Label><Input type="tel" data-testid="new-prospect-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 123 4567" /></div>
          <div><Label>Company</Label><Input value={form.company} onChange={(e) => set('company', e.target.value)} /></div>
          <div><Label>Role</Label><Input value={form.role} onChange={(e) => set('role', e.target.value)} /></div>
          <div className="col-span-2"><Label>Location</Label><Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Tbilisi, Georgia · Napa, CA · London, UK" /></div>
          <div>
            <Label>Source</Label>
            <Select value={form.source} onValueChange={(v) => set('source', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Source detail (URL, name, etc)</Label><Input value={form.sourceDetail} onChange={(e) => set('sourceDetail', e.target.value)} placeholder="e.g. article URL or referrer name" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="text-white" style={{ background: activeShow.color }}>{saving ? 'Adding…' : 'Add prospect'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
